import { makeAutoObservable } from "mobx";
import MixpanelService, {
    IMixPanelIdentity,
    MixpanelOptionalProperties
} from "../../../services/mixpanel.service";
import { rootContainer, TYPE } from "../../../services/container/root";
import { ONBOARDING_STEPS } from "../constants";


export enum ONBOARDING_MIXPANEL_EVENT {

    /** OnBoarding status events */
    ONBOARDING_STARTED = 'onboarding-started',
    ONBOARDING_CONTINUED = 'onboarding-continued',
    ONBOARDING_COMPLETED = 'onboarding-completed',
    ONBOARDING_ATTEMPT_FAILED = 'onboarding-attempt-failed',

    /** OnBoarding navigation events */
    ONBOARDING_PREV_STEP = 'onboarding-previous-step',
    ONBOARDING_NEXT_STEP = 'onboarding-next-step',
    ONBOARDING_BUTTON_CLICKED = 'onboarding-button-clicked',

    /** OnBoarding data events */
    ONBOARDING_DATA_COLLECTED = 'onboarding-data-collected',

    /** OnBoarding view appearance events */
    ONBOARDING_NEW_USER_WELCOME_SCREEN_VIEW = "onboarding-new-user-welcome-view",
    ONBOARDING_ACCEPT_TERMS_SCREEN_VIEW = "onboarding-accept-terms-view",
    ONBOARDING_CHOOSE_PLAN_TYPE_SCREEN_VIEW = "onboarding-choose-plan-type-view",
    ONBOARDING_CHOOSE_PLAN_SCREEN_VIEW = "onboarding-choose-plan-view",
    ONBOARDING_CHOOSE_RETURN_TYPE_SCREEN_VIEW = "onboarding-choose-return-type-view",
    ONBOARDING_CREATE_RETURN_METHOD_SCREEN_VIEW = "onboarding-create-return-method-view",
    ONBOARDING_WALLET_CONFIGURATION_SCREEN_VIEW = "onboarding-wallet-configuration-view",
    ONBOARDING_ENABLE_RETURN_GUARD_SCREEN_VIEW = "onboarding-enable-return-guard-view",
    ONBOARDING_SETUP_RETURN_GUARD_SCREEN_VIEW = "onboarding-setup-return-guard-view",
    ONBOARDING_APPROVE_PLAN_SCREEN_VIEW = "onboarding-approve-plan-view",
    ONBOARDING_FINAL_SCREEN_VIEW = "onboarding-final-view",

    /** OnBoarding customer support events */
    ONBOARDING_CUSTOMER_SUPPORT_LINK_CLICKED = 'onboarding-customer-support-link-clicked'
}

export enum ONBOARDING_BUTTON_TRACKABLE_TYPES {
    ONBOARDING_ENABLE_RETURN_GUARD_SETUP_NOW = 'onboarding-enable-return-guard-setup-now',
    ONBOARDING_ENABLE_RETURN_GUARD_SETUP_LATER = 'onboarding-enable-return-guard-setup-later',
    ONBOARDING_SETUP_RETURN_GUARD_SETUP_LATER = 'onboarding-setup-return-guard-setup-later',
    ONBOARDING_SETUP_RETURN_GUARD_SETUP_NOW = 'onboarding-setup-return-guard-setup-now'
}

const MAP_STEP_TO_SCREEN_VIEW_EVENT: Record<ONBOARDING_STEPS, ONBOARDING_MIXPANEL_EVENT> = {
    [ONBOARDING_STEPS.NEW_USER_WELCOME]: ONBOARDING_MIXPANEL_EVENT.ONBOARDING_NEW_USER_WELCOME_SCREEN_VIEW,
    [ONBOARDING_STEPS.ACCEPT_TERMS]: ONBOARDING_MIXPANEL_EVENT.ONBOARDING_ACCEPT_TERMS_SCREEN_VIEW,
    [ONBOARDING_STEPS.CHOOSE_PLAN_TYPE]: ONBOARDING_MIXPANEL_EVENT.ONBOARDING_CHOOSE_PLAN_TYPE_SCREEN_VIEW,
    [ONBOARDING_STEPS.CHOOSE_PLAN]: ONBOARDING_MIXPANEL_EVENT.ONBOARDING_CHOOSE_PLAN_SCREEN_VIEW,
    [ONBOARDING_STEPS.CHOOSE_RETURN_TYPE]: ONBOARDING_MIXPANEL_EVENT.ONBOARDING_CHOOSE_RETURN_TYPE_SCREEN_VIEW,
    [ONBOARDING_STEPS.CREATE_RETURN_METHOD]: ONBOARDING_MIXPANEL_EVENT.ONBOARDING_CREATE_RETURN_METHOD_SCREEN_VIEW,
    [ONBOARDING_STEPS.WALLET_CONFIGURATION]: ONBOARDING_MIXPANEL_EVENT.ONBOARDING_WALLET_CONFIGURATION_SCREEN_VIEW,
    [ONBOARDING_STEPS.ENABLE_RETURN_GUARD]: ONBOARDING_MIXPANEL_EVENT.ONBOARDING_ENABLE_RETURN_GUARD_SCREEN_VIEW,
    [ONBOARDING_STEPS.SETUP_RETURN_GUARD]: ONBOARDING_MIXPANEL_EVENT.ONBOARDING_SETUP_RETURN_GUARD_SCREEN_VIEW,
    [ONBOARDING_STEPS.APPROVE_PLAN]: ONBOARDING_MIXPANEL_EVENT.ONBOARDING_APPROVE_PLAN_SCREEN_VIEW,
    [ONBOARDING_STEPS.FINAL]: ONBOARDING_MIXPANEL_EVENT.ONBOARDING_FINAL_SCREEN_VIEW,
}

class OnboardingMixPanelStore {
    private _identity?: IMixPanelIdentity;
    private _properties: Record<string, any> = {};

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get _mixpanelService(): MixpanelService {
        return rootContainer.get<MixpanelService>(TYPE.MixpanelService);
    }

    public setIdentity(identify: IMixPanelIdentity): void {
        this._identity = identify;
        this._properties.shop = identify.shop_name;
    }

    public setOnboardingModelId(id: number): void {
        this._properties.onboarding_id = id;
    }

    public trackEvent(event: ONBOARDING_MIXPANEL_EVENT, properties: MixpanelOptionalProperties): void {
        /** wrapped in try catch to make sure it will not break functionality */
        try {
            this._mixpanelService.trackEvent(event, { ...this._properties, ...properties, event_timestamp: Date.now() }, { ...this._identity });
        }
        catch (e) {
            console.error(e);
        }
    }

    public trackScreenView(step: ONBOARDING_STEPS): void {
        this.trackEvent(MAP_STEP_TO_SCREEN_VIEW_EVENT[step], { from: step });
    }

    public startMixpanelSessionRecording() {
        this._mixpanelService.startSessionRecording({...this._identity});
    }

    public stopMixpanelSessionRecording() {
        this._mixpanelService.stopSessionRecording({...this._identity});
    }
}

const onboardingMixPanelStore = new OnboardingMixPanelStore();
export default onboardingMixPanelStore;
